import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { env } from '@/lib/env'
import { isDemo } from '@/lib/demo'

interface ServerMessage {
  messageType: 'info' | 'error' | 'success' | string
  message: string
}

/** Server pushes (import finished, sync failed, …) arrive as toasts and refresh the bell. */
export function useLiveUpdates(userId: string | undefined) {
  const qc = useQueryClient()

  useEffect(() => {
    if (!userId || !env.wsBaseUrl || isDemo()) return
    let socket: WebSocket | null = null
    let retry: ReturnType<typeof setTimeout> | undefined
    let closed = false
    let attempt = 0

    const connect = () => {
      socket = new WebSocket(`${env.wsBaseUrl}/ws?userID=${encodeURIComponent(userId)}`)
      socket.onopen = () => (attempt = 0)
      socket.onmessage = (event) => {
        let msg: ServerMessage
        try {
          msg = JSON.parse(event.data)
        } catch {
          return
        }
        if (msg.messageType === 'success') toast.success(msg.message)
        else if (msg.messageType === 'error') toast.error(msg.message)
        else if (msg.messageType === 'info') toast.info(msg.message)
        qc.invalidateQueries({ queryKey: ['notifications'] })
      }
      // v1 never reconnected; back off up to 30s so a restarted API is picked up again
      socket.onclose = () => {
        if (closed) return
        retry = setTimeout(connect, Math.min(30_000, 1000 * 2 ** attempt++))
      }
    }
    connect()

    return () => {
      closed = true
      clearTimeout(retry)
      socket?.close()
    }
  }, [userId, qc])
}
